import DebugInfo from "@components/Debug/Info";
import LocationMap from "@components/LocationMap";
import Dialog from "@hudson/ui/components/Dialogs/Simple";
import debugContext from "@lib/contexts/debug";
import useContent from "@lib/use_content";
import { useContext, useState } from "react";
import AmenityLegend from "./AmenityLegend";
import NearbyLocation from "./NearbyLocation";

export default function NearbyLocationsDialog({
  anchorPoint,
  locations,
  onHide,
  onLocationSelect,
  requestedLocation,
  show,
}: {
  anchorPoint: be.Location;
  locations: be.Location[];
  onHide: () => void;
  onLocationSelect: (loc: be.Location) => void;
  requestedLocation?: be.Location;
  show: boolean;
}): JSX.Element {
  const { debugMode } = useContext(debugContext);
  const { data, loading, error } = useContent([
    "beNearbyLocsHeaderPrefix",
    "beNearbyLocsHeaderMsgLocAvailable",
    "beNearbyLocsHeaderMsgLocNotAvailable",
    "beNearbyLocsSectHeaderNearbyLocs",
    "beNearbyLocsSectHeaderReqLoc",
    "beNearbyLocsSectSubheadNearbyLocs",
    "beNearbyLocsSectSubheadReqLoc",
  ]);
  const [hoverLoc, setHoverLoc] = useState<be.Location | null>(null);

  const amenityTypes = [...new Set(locations.map((loc) => loc.icons).flat())];
  return (
    <Dialog
      show={show}
      maxWidthClass="max-w-4xl"
      onHide={onHide}
      pagePath={`book/nearbyLocationsPopup`}
      title="Nearby Locations"
    >
      <>
        <h1 className="flex mb-4 text-xl sm:text-3xl tracking-tight font-bold text-gray-900 ">
          {data?.content.beNearbyLocsHeaderPrefix} {anchorPoint.displayName}
        </h1>

        <p className="mt-4 mb-2 sm:mb-6 text-xl leading-8 text-gray-500 w-full sm:w-3/4">
          {requestedLocation
            ? data?.content.beNearbyLocsHeaderMsgLocAvailable
            : data?.content.beNearbyLocsHeaderMsgLocNotAvailable}
        </p>

        {debugMode && anchorPoint && (
          <DebugInfo
            className="font-bold text-sm -mt-4 mb-6 inline-block"
            dialogContent={
              <>
                {anchorPoint.fklRequest && (
                  <>
                    <div className="font-bold text-lg mt-3">fareKeyLookup Request</div>
                    <pre>{JSON.stringify(JSON.parse(anchorPoint.fklRequest), null, 2)}</pre>
                  </>
                )}

                {anchorPoint.fklResponse && (
                  <>
                    <div className="font-bold text-lg mt-3">fareKeyLookup Response</div>
                    <pre>{JSON.stringify(JSON.parse(anchorPoint.fklResponse), null, 2)}</pre>
                  </>
                )}
              </>
            }
          >
            Location details
          </DebugInfo>
        )}

        <div className="md:flex space-x-6 overflow-y-scroll">
          <div className="w-full flex flex-col-reverse sm:flex-col">
            <div className="h-96 sm:h-[500px]">
              <LocationMap
                currentLocation={requestedLocation ? undefined : anchorPoint}
                hoverLoc={hoverLoc}
                locations={requestedLocation ? [requestedLocation, ...locations] : locations}
                selectLocation={(loc) => {
                  onHide();
                  onLocationSelect(loc);
                }}
                width="100%"
                height="100%"
              />
            </div>
            <AmenityLegend types={amenityTypes} />
          </div>

          <div className="mt-6 md:mt-0">
            {requestedLocation && (
              <>
                <h3 className="text-base font-bold text-zinc-900">
                  {data?.content.beNearbyLocsSectHeaderReqLoc}
                </h3>
                <h2 className="text-sm  text-zinc-500">
                  {data?.content.beNearbyLocsSectSubheadReqLoc}
                </h2>

                <div className="columns-1 space-y-6 md:w-80 mb-6 mt-2">
                  <NearbyLocation
                    index={1}
                    location={requestedLocation}
                    onHover={setHoverLoc}
                    onSelect={() => {
                      onHide();
                      onLocationSelect(requestedLocation);
                    }}
                  />
                </div>
                <hr className="w-64 h-0.5 mx-auto my-4 sm:my-0 bg-gray-100 border-0 rounded md:my-4 dark:bg-gray-700" />
              </>
            )}

            {requestedLocation && (
              <div className="mb-3">
                <h3 className="text-base font-bold text-zinc-900">
                  {data?.content.beNearbyLocsSectHeaderNearbyLocs}
                </h3>
                <h2 className="text-sm  text-zinc-500">
                  {data?.content.beNearbyLocsSectSubheadNearbyLocs}
                </h2>
              </div>
            )}
            <div className="columns-1 xs:columns-1 sm:columns-2 md:columns-1 space-y-6 md:w-80">
              {locations.slice(0, 5).map((loc: be.Location, i) => (
                <NearbyLocation
                  index={requestedLocation ? i + 2 : i + 1}
                  location={loc}
                  key={i}
                  onHover={setHoverLoc}
                  onSelect={() => {
                    onHide();
                    onLocationSelect(loc);
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </>
    </Dialog>
  );
}
